import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceRef,
  Signal,
} from '@angular/core';
import { IImage, ImageId } from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { AppConfigService } from '../../app-config.service';
import { ImageGateway } from './image.gateway';

@Injectable({
  providedIn: 'root',
})
export class ImageStore {
  private readonly configService: AppConfigService = inject(AppConfigService);

  private readonly gateway: ImageGateway = new ImageGateway();

  readonly #imageResource: ResourceRef<IApiResponse<IImage[]> | undefined> =
    resource({
      loader: (): Promise<IApiResponse<IImage[]>> => {
        return firstValueFrom(this.gateway.get());
      },
    });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#imageResource.isLoading()
  );
  readonly images: Signal<IImage[]> = computed((): IImage[] =>
    this.#imageResource.hasValue()
      ? (this.#imageResource.value()?.data ?? [])
      : []
  );

  loadImage(): void {
    this.#imageResource.reload();
  }

  getFile(imageId: ImageId): Promise<Blob> {
    return firstValueFrom(this.gateway.getFile(imageId));
  }

  updateImage(query: IImage[]): void {
    this.#imageResource.update((): IApiResponse<IImage[]> => {
      return {
        data: query,
      };
    });
  }

  reload(): void {
    this.#imageResource.reload();
  }

  upload(query: FormData): Promise<void> {
    return firstValueFrom(this.gateway.upload(query)).then(
      (result: IApiResponse<IImage[]>) => {
        if (result?.data) {
          this.updateImage(result.data);
        }
      }
    );
  }

  delete(imageId: ImageId): Promise<void> {
    return firstValueFrom(this.gateway.delete(imageId)).then(
      (result: IApiResponse<IImage[]>) => {
        if (result?.data) {
          this.reload();
        }
      }
    );
  }

  save(query: IImage[]): Promise<void> {
    return firstValueFrom(this.gateway.save(query)).then(
      (result: IApiResponse<IImage[]>) => {
        if (result?.data) {
          this.updateImage(result.data);
        }
      }
    );
  }

  getThumbnailPath(id: string | undefined): string {
    if (id !== undefined) {
      return (
        this.configService.get().general.cmsUrl + '/images/' + id + '/thumbnail'
      );
    }
    return '';
  }

  getImagePath(id: string | undefined): string {
    return this.configService.get().general.cmsUrl + '/images/' + id;
  }
}
